import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, createBlock as _createBlock, createTextVNode as _createTextVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "group-header" }
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = ["value", "title"]
const _hoisted_4 = { class: "group-header-meta" }
const _hoisted_5 = ["title"]
const _hoisted_6 = { class: "group-duration" }
const _hoisted_7 = { class: "group-flow-ports" }
const _hoisted_8 = { class: "group-input-label" }
const _hoisted_9 = ["title"]
const _hoisted_10 = { class: "group-output-text" }
const _hoisted_11 = {
  key: 0,
  class: "shared-focus-badge",
  title: "Focus Push 到达触发时间后会强制使用此出口"
}
const _hoisted_12 = {
  key: 0,
  class: "group-port-row no-flow-output"
}
const _hoisted_13 = { class: "group-input-label" }
const _hoisted_14 = { class: "group-content" }
const _hoisted_15 = ["title"]
const _hoisted_16 = { class: "group-stats" }
const _hoisted_17 = { key: 0 }
const _hoisted_18 = {
  key: 0,
  class: "timeline-preview",
  "aria-hidden": "true"
}

import { computed, nextTick, onMounted, watch } from "vue";
import {
  Handle,
  Position,
  useVueFlow,
  type Edge,
} from "@vue-flow/core";
import type { DialogueNode } from "./types/DialogueNode";
import {
  getGroupOutletWarnings,
  resolveGroupOutlets,
} from "./utils/groupOutlets";
import {
  getFlowClipDuration,
  getGroupTimelineEnd,
  getPerformanceClipDuration,
} from "./utils/groupTimeline";
import { getDialogueClips } from "./utils/dialogueClips";


export default /*@__PURE__*/_defineComponent({
  __name: 'GroupNode',
  props: {
    id: {},
    node: {},
    edges: { default: () => [] },
    selected: { type: Boolean, default: false },
    exportIndex: {},
    annotation: { default: "" }
  },
  emits: ["update:annotation"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

function updateAnnotation(event: Event) {
  emit("update:annotation", (event.target as HTMLInputElement).value);
}

const { updateNodeInternals } = useVueFlow();

function toSeconds(value: unknown) {
  const seconds = Number(value);
  return Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
}

const performanceClips = computed(() =>
  props.node.lines.flatMap((line) => line.clips),
);
const dialogues = computed(() => getDialogueClips(props.node));
const dialogueSummary = computed(() => dialogues.value.map(clip =>
  `${clip.speaker ? `${clip.speaker}：` : ""}${clip.content}`,
).join(" / "));
const autoDuration = computed(() => getGroupTimelineEnd(props.node));

const duration = computed(() =>
  props.node.durationMode === "Fixed"
    ? Math.max(toSeconds(props.node.duration), autoDuration.value)
    : autoDuration.value,
);

const durationLabel = computed(() => `${duration.value.toFixed(1)}s`);
const outletState = computed(() => resolveGroupOutlets(props.node));
const outlets = computed(() => outletState.value.outlets);
const outletWarnings = computed(() =>
  getGroupOutletWarnings(props.node),
);
const outletSignature = computed(() =>
  outlets.value.map((outlet) => outlet.id).join("|"),
);

async function refreshHandles() {
  await nextTick();
  updateNodeInternals([props.id]);
}

watch(outletSignature, refreshHandles, { flush: "post" });
onMounted(refreshHandles);

const previewSegments = computed(() => {
  const visibleClips = [
    ...(props.node.select ? [props.node.select] : []),
    ...performanceClips.value,
  ].slice(0, 6);
  const total = Math.max(duration.value, 0.25);

  return visibleClips.map((clip, index) => ({
    id: `${props.id}-${index}`,
    left: `${(toSeconds(clip.startTime) / total) * 100}%`,
    width: `${
      (Math.max(
        "continueDelayTime" in clip
          ? getFlowClipDuration(props.node, clip)
          : toSeconds(getPerformanceClipDuration(clip)),
        0.25,
      ) /
        total) *
      100
    }%`,
  }));
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", {
    class: _normalizeClass(["group-node", { selected: __props.selected }])
  }, [
    _createElementVNode("header", _hoisted_1, [
      (__props.exportIndex !== undefined)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: "export-index",
            "aria-label": `TimeLineGroup 索引 ${__props.exportIndex}`,
            title: "导出后的 TimeLineGroup 全局索引，从 0 开始，可用于播放调试"
          }, "#" + _toDisplayString(__props.exportIndex), 9, _hoisted_2))
        : _createCommentVNode("", true),
      _createElementVNode("input", {
        class: "nodrag group-kind graph-node-annotation",
        value: __props.annotation,
        "aria-label": "节点备注（仅编辑器显示，不参与导出）",
        title: __props.annotation || '点击填写节点备注，仅保存在工程中',
        placeholder: "普通事件节点",
        onInput: updateAnnotation,
        onPointerdown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
        onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
        onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
        onKeydown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
      }, null, 40, _hoisted_3),
      _createElementVNode("div", _hoisted_4, [
        (outletWarnings.value.length)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              class: "group-warning",
              title: outletWarnings.value.join('\n')
            }, "⚠ " + _toDisplayString(outletWarnings.value.length), 9, _hoisted_5))
          : _createCommentVNode("", true),
        _createElementVNode("span", _hoisted_6, _toDisplayString(durationLabel.value), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(outlets.value, (outlet, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: outlet.id,
          class: _normalizeClass(["group-port-row", `outlet-${outlet.kind.toLowerCase()}`])
        }, [
          _createElementVNode("span", _hoisted_8, [
            (index === 0)
              ? (_openBlock(), _createBlock(_unref(Handle), {
                  key: 0,
                  id: "input",
                  class: "group-input-handle",
                  type: "target",
                  position: _unref(Position).Left,
                  title: "Group 入口",
                  "aria-label": "Group 入口"
                }, null, 8, ["position"]))
              : _createCommentVNode("", true),
            (index === 0)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode("入口")
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("span", {
            class: "group-output-label",
            title: outlet.label
          }, [
            _createElementVNode("span", _hoisted_10, _toDisplayString(outlet.label), 1),
            (__props.node.focusPush?.outputMode === 'Shared' && __props.node.focusPush.sharedOutletIndex === index)
              ? (_openBlock(), _createElementBlock("span", _hoisted_11, "强制共用"))
              : _createCommentVNode("", true)
          ], 8, _hoisted_9),
          _createVNode(_unref(Handle), {
            id: outlet.id,
            class: "group-output-handle",
            type: "source",
            position: _unref(Position).Right,
            title: outlet.label,
            "aria-label": outlet.label
          }, null, 8, ["id", "position", "title", "aria-label"])
        ], 2))
      }), 128)),
      (!outlets.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _createElementVNode("span", _hoisted_13, [
              _createVNode(_unref(Handle), {
                id: "input",
                class: "group-input-handle",
                type: "target",
                position: _unref(Position).Left,
                title: "Group 入口",
                "aria-label": "Group 入口"
              }, null, 8, ["position"]),
              _cache[4] || (_cache[4] = _createTextVNode(" 入口 ", -1))
            ]),
            _cache[5] || (_cache[5] = _createElementVNode("span", null, "⚠ 无流程出口", -1))
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("section", _hoisted_14, [
      _createElementVNode("div", {
        class: _normalizeClass(["group-dialogue", { empty: !dialogues.value.length }]),
        title: dialogueSummary.value
      }, [
        (dialogues.value.length)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createTextVNode(_toDisplayString(dialogueSummary.value), 1)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createTextVNode("暂无 Dialogue Clip")
            ], 64))
      ], 10, _hoisted_15),
      _createElementVNode("div", _hoisted_16, [
        (dialogues.value.length > 1)
          ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(dialogues.value.length) + " 台词", 1))
          : _createCommentVNode("", true),
        _createElementVNode("span", null, _toDisplayString(performanceClips.value.length) + " 演出", 1),
        _createElementVNode("span", null, _toDisplayString(__props.node.select?.options.length ?? 0) + " 选项", 1)
      ]),
      (previewSegments.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(previewSegments.value, (segment, index) => {
              return (_openBlock(), _createElementBlock("span", {
                key: segment.id,
                class: _normalizeClass(["timeline-segment", `segment-${(index % 3) + 1}`]),
                style: _normalizeStyle({ left: segment.left, width: segment.width })
              }, null, 6))
            }), 128))
          ]))
        : _createCommentVNode("", true)
    ])
  ], 2))
}
}

})